import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PsychologyIcon from '@mui/icons-material/Psychology'
import AssignmentIcon from '@mui/icons-material/Assignment'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonIcon from '@mui/icons-material/Person'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import HistoryIcon from '@mui/icons-material/History'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import usePrescriptionInterventions from './hooks/usePrescriptionInterventions'
import ModernizedFilters from './components/ModernizedFilters'
import InterventionActionDrawer from './components/InterventionActionDrawer'
import FollowupsDrawer from './components/FollowupsDrawer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import relativeTime from 'dayjs/plugin/relativeTime'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { getExactDiffFromCurrentTimeDayJs } from 'src/consumer/helpers/utilFunctions'

dayjs.extend(utc)
dayjs.extend(relativeTime)

const InterventionCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
  },
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  width: '100%',
}))

const EmptyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  width: '100%',
  textAlign: 'center',
}))

export default function PrescriptionInterventionsListing() {
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedIntervention, setSelectedIntervention] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [followupsDrawerOpen, setFollowupsDrawerOpen] = useState(false)
  const [selectedInterventionForFollowups, setSelectedInterventionForFollowups] = useState(null)

  // Parse URL query parameters for pre-populated filters
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search)
    const initialFilters = {}

    // Core filters from URL
    if (params.get('clinicLocationId')) {
      initialFilters.clinicLocationId = params.get('clinicLocationId')
    }
    if (params.get('clinicLocationSlug')) {
      initialFilters.clinicLocationSlug = params.get('clinicLocationSlug')
    }
    if (params.get('triggeringDoctorId')) {
      initialFilters.triggeringDoctorId = params.get('triggeringDoctorId')
      initialFilters.mentorId = params.get('triggeringDoctorId')
    }
    if (params.get('triggeringDoctorSlug')) {
      initialFilters.triggeringDoctorSlug = params.get('triggeringDoctorSlug')
    }
    if (params.get('patientId')) {
      initialFilters.patientId = params.get('patientId')
    }

    return initialFilters
  }

  const {
    interventions,
    loading,
    error,
    pagination,
    filters,
    updateFilter,
    updateFilters,
    clearAllFilters,
    changePage,
    changePageSize,
  } = usePrescriptionInterventions(getInitialFilters())

  // Pre-populate filters from URL on mount
  useEffect(() => {
    const initialFilters = getInitialFilters()
    if (Object.keys(initialFilters).length > 0) {
      updateFilters(initialFilters)
    }
  }, [location.search])

  const handleFilterChange = useCallback((key, value) => {
    updateFilter(key, value)
  }, [updateFilter])

  const handleOpenDrawer = (intervention) => {
    setSelectedIntervention(intervention)
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedIntervention(null)
  }

  const handleOpenFollowupsDrawer = (intervention) => {
    setSelectedInterventionForFollowups(intervention)
    setFollowupsDrawerOpen(true)
  }

  const handleCloseFollowupsDrawer = () => {
    setFollowupsDrawerOpen(false)
    setSelectedInterventionForFollowups(null)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      // Parse the date string in format 'yyyy-MM-dd HH:mm:ss' as UTC and convert to local time
      // The API returns eventStartTime in UTC format 'yyyy-MM-dd HH:mm:ss' (consistent with rest of app)
      let date = dayjs.utc(dateString, MOMENT_FORMATS.formatThatServerSends, true) // strict parsing
      
      // If strict parsing fails, try without format (auto-detect)
      if (!date.isValid()) {
        date = dayjs.utc(dateString)
      }
      
      // If still invalid, try parsing as local time
      if (!date.isValid()) {
        date = dayjs(dateString)
      }
      
      if (!date.isValid()) {
        return dateString // Return original string if all parsing fails
      }
      
      // Convert UTC to local time and format (consistent with other support portal listings)
      return date.local().format('DD MMM YYYY, HH:mm')
    } catch {
      return dateString
    }
  }

  const getDateInfo = (dateString) => {
    if (!dateString) return { date: null, relativeLabel: null, formattedDate: 'N/A' }
    try {
      let date = dayjs.utc(dateString, MOMENT_FORMATS.formatThatServerSends, true)
      if (!date.isValid()) {
        date = dayjs.utc(dateString)
      }
      if (!date.isValid()) {
        date = dayjs(dateString)
      }
      if (!date.isValid()) {
        return { date: null, relativeLabel: null, formattedDate: dateString }
      }
      
      const localDate = date.local()
      const now = dayjs()
      const daysDiff = localDate.startOf('day').diff(now.startOf('day'), 'day')
      
      let relativeLabel = null
      if (daysDiff === 0) {
        relativeLabel = 'Today'
      } else if (daysDiff === -1) {
        relativeLabel = 'Yesterday'
      } else if (daysDiff < -1) {
        const daysAgo = Math.abs(daysDiff)
        relativeLabel = `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`
      } else if (daysDiff === 1) {
        relativeLabel = 'Tomorrow'
      } else {
        relativeLabel = `In ${daysDiff} days`
      }
      
      return {
        date: localDate,
        relativeLabel,
        formattedDate: localDate.format('DD MMM'),
        formattedTime: localDate.format('HH:mm'),
        dayName: localDate.format('ddd'),
      }
    } catch {
      return { date: null, relativeLabel: null, formattedDate: dateString }
    }
  }

  const hasTherapyIntervention = (intervention) => {
    return (
      intervention?.therapy?.prescribedTherapy?.length > 0 ||
      intervention?.therapy?.therapiesNeeded?.length > 0 ||
      intervention?.therapy?.prescribedActions?.length > 0 ||
      intervention?.therapy?.suggestedBookingTherapy ||
      intervention?.therapy?.requestedTherapist ||
      intervention?.therapy?.notesToTherapist
    )
  }

  const getStageLabel = (stageKey) => {
    const stageLabels = {
      NEW: 'New',
      TRIAGE: 'Triage',
      ASSIGN: 'Assign Therapist',
      CONTACT: 'Contact Patient',
      SCHEDULE: 'Schedule Session',
      PAYMENT: 'Payment',
      REMINDER: 'Reminder',
      CLOSE: 'Close Loop',
    }
    return stageLabels[stageKey] || stageKey
  }

  const getStageColor = (stageKey) => {
    const stageColors = {
      NEW: 'default',
      TRIAGE: 'info',
      ASSIGN: 'primary',
      CONTACT: 'warning',
      SCHEDULE: 'secondary',
      PAYMENT: 'success',
      REMINDER: 'info',
      CLOSE: 'default',
    }
    return stageColors[stageKey] || 'default'
  }

  const hasAssessmentIntervention = (intervention) => {
    return (
      intervention?.assessment?.prescribedAssessments?.length > 0 ||
      intervention?.assessment?.psychAssessments?.length > 0 ||
      intervention?.assessment?.notesForAssessment
    )
  }

  return (
    <>
      <SupportHeader heading="Prescription Interventions" showBackButton />
      <Box
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '24px', md: '32px' },
          background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
          minHeight: 'calc(100vh - 200px)',
        }}
      >
        <Box
          sx={{
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          <ModernizedFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={clearAllFilters}
            sort={filters.sort || 'startTime,desc'}
            onSortChange={(newSort) => handleFilterChange('sort', newSort)}
          />

          {error && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {loading ? (
            <LoadingContainer>
              <CircularProgress size={40} />
            </LoadingContainer>
          ) : interventions.length === 0 ? (
            <EmptyContainer>
              <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No interventions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Object.keys(filters).some((key) => key !== 'sort' && filters[key])
                  ? 'Try adjusting your filters to see more results.'
                  : 'There are no prescription interventions available at this time.'}
              </Typography>
            </EmptyContainer>
          ) : (
            <>
              <Stack spacing={2} sx={{ mb: 3 }}>
                {interventions.map((intervention) => {
                  const dateInfo = getDateInfo(intervention?.eventStartTime)
                  return (
                    <InterventionCard 
                      key={intervention.prescriptionId}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenFollowupsDrawer(intervention)
                      }}
                    >
                      <CardContent
                        sx={{
                          padding: { xs: theme.spacing(2), sm: theme.spacing(2.5), md: theme.spacing(3) },
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          width: '100%', 
                          gap: { xs: 1.5, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                        }}>
                          {/* Date Strip - Left Side */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              minWidth: { xs: '80px', sm: '100px' },
                              width: { xs: '80px', sm: '100px' },
                              padding: { xs: '8px 4px', sm: '12px 8px' },
                              background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                              borderRadius: '12px',
                              border: '1px solid rgba(67, 56, 202, 0.15)',
                              flexShrink: 0,
                            }}
                          >
                            {dateInfo.date && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    color: 'primary.main',
                                    mb: 0.5,
                                  }}
                                >
                                  {dateInfo.dayName}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: { xs: '20px', sm: '24px' },
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    lineHeight: 1.2,
                                    mb: 0.5,
                                  }}
                                >
                                  {dateInfo.formattedDate.split(' ')[0]}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    mb: 1,
                                  }}
                                >
                                  {dateInfo.formattedDate.split(' ')[1]}
                                </Typography>
                                {dateInfo.relativeLabel && (
                                  <Chip
                                    label={dateInfo.relativeLabel}
                                    size="small"
                                    sx={{
                                      height: '20px',
                                      fontSize: '9px',
                                      fontWeight: 600,
                                      backgroundColor: dateInfo.relativeLabel === 'Today' 
                                        ? 'success.light' 
                                        : dateInfo.relativeLabel === 'Yesterday'
                                        ? 'info.light'
                                        : 'grey.200',
                                      color: dateInfo.relativeLabel === 'Today'
                                        ? 'success.dark'
                                        : dateInfo.relativeLabel === 'Yesterday'
                                        ? 'info.dark'
                                        : 'text.secondary',
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    mt: 0.5,
                                  }}
                                >
                                  {dateInfo.formattedTime}
                                </Typography>
                              </>
                            )}
                          </Box>

                          {/* Main Content */}
                          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                            {/* Main Info Row */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                gap: { xs: 1.5, sm: 2 },
                                width: '100%',
                                mb: { xs: 2, sm: 0 },
                                flexWrap: { xs: 'nowrap', md: 'wrap' },
                              }}
                            >
                              {/* Patient Info */}
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: { xs: 1.5, sm: 2 }, 
                                flex: { xs: '1 1 100%', sm: '1 1 auto' },
                                minWidth: 0,
                                maxWidth: { xs: '100%', sm: 'none' },
                              }}>
                                <Avatar
                                  src={getImageLink(intervention?.patient?.profilePicId)}
                                  sx={{ 
                                    width: { xs: 48, sm: 56 }, 
                                    height: { xs: 48, sm: 56 },
                                    flexShrink: 0,
                                  }}
                                >
                                  {intervention?.patient?.name?.charAt(0)?.toUpperCase() || 'P'}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight={600} 
                                    sx={{
                                      fontSize: { xs: '1rem', sm: '1.25rem' },
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {intervention?.patient?.name || 'Unknown Patient'}
                                  </Typography>
                                  {intervention?.patient?.patientNumber && (
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Patient #: {intervention.patient.patientNumber}
                                    </Typography>
                                  )}
                                  {intervention?.patient?.mobile && (
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {intervention.patient.mobile}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* Mentor Info */}
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5, 
                                flex: { xs: '1 1 100%', sm: '0 0 auto' },
                                minWidth: { xs: '100%', sm: '180px', md: '200px' },
                                maxWidth: { xs: '100%', sm: 'none' },
                              }}>
                                <PersonIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 }, flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {intervention?.mentor?.name || 'Unknown Doctor'}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                  >
                                    Triggering Doctor
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Event Mode Info */}
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1.5, 
                                flex: { xs: '1 1 100%', sm: '0 0 auto' },
                                minWidth: { xs: '100%', sm: '100px', md: '120px' },
                                maxWidth: { xs: '100%', sm: 'none' },
                              }}>
                                <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 }, flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {intervention?.eventMode || 'N/A'}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                  >
                                    {intervention?.eventType || 'Consultation'}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Clinic Info */}
                              {intervention?.clinicLocationSlug && (
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1.5, 
                                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                                  minWidth: { xs: '100%', sm: '150px', md: '180px' },
                                  maxWidth: { xs: '100%', sm: 'none' },
                                }}>
                                  <LocalHospitalIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 }, flexShrink: 0 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight={500}
                                      sx={{
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {intervention?.clinicBrandSlug || intervention?.clinicLocationSlug}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {intervention?.clinicLocationSlug}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                        </Box>

                        {/* Followup Summary */}
                        {intervention?.followup && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Stack spacing={1.5}>
                              {/* Current Stage */}
                              {intervention.followup.currentStageKey && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <HistoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                  <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                      <Typography variant="caption" fontWeight={600} color="text.primary">
                                        Current Stage:
                                      </Typography>
                                      <Chip
                                        label={getStageLabel(intervention.followup.currentStageKey)}
                                        size="small"
                                        color={getStageColor(intervention.followup.currentStageKey)}
                                        sx={{
                                          height: '22px',
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                        }}
                                      />
                                      {intervention.followup.currentStageStatus && (
                                        <Chip
                                          label={intervention.followup.currentStageStatus}
                                          size="small"
                                          variant="outlined"
                                          color={
                                            intervention.followup.currentStageStatus === 'done'
                                              ? 'success'
                                              : intervention.followup.currentStageStatus === 'in-progress'
                                              ? 'warning'
                                              : 'default'
                                          }
                                          sx={{
                                            height: '22px',
                                            fontSize: '0.7rem',
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  </Box>
                                </Box>
                              )}
                              
                              {/* Latest Support Agent */}
                              {intervention.followup.lastFollowupByName && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Latest Support Agent: <strong>{intervention.followup.lastFollowupByName}</strong>
                                    </Typography>
                                    {intervention.followup.lastFollowupAt && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                        Last followup: {formatDate(intervention.followup.lastFollowupAt)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                              
                              {/* Followup Count */}
                              {intervention.followup.followupCount > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={`${intervention.followup.followupCount} followup${intervention.followup.followupCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: '22px', fontSize: '0.7rem' }}
                                  />
                                </Box>
                              )}
                              
                              {/* No followups message */}
                              {intervention.followup.followupCount === 0 && !intervention.followup.currentStageKey && (
                                <Typography variant="caption" color="text.secondary">
                                  No followups yet
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}

                        {/* Intervention Details Section */}
                        {(hasTherapyIntervention(intervention) || hasAssessmentIntervention(intervention)) && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 2, sm: 3 },
                              }}
                            >
                              {/* Therapy Section */}
                              {hasTherapyIntervention(intervention) && (
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="caption"
                                    color="primary"
                                    sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                                  >
                                    <PsychologyIcon sx={{ fontSize: 16 }} />
                                    Therapy
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {intervention?.therapy?.therapiesNeeded?.map((therapy, idx) => (
                                      <Chip
                                        key={`therapy-needed-${idx}`}
                                        label={therapy}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: '22px' }}
                                      />
                                    ))}
                                    {intervention?.therapy?.prescribedTherapy?.map((therapy, idx) => (
                                      <Chip
                                        key={`prescribed-therapy-${idx}`}
                                        label={therapy}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: '22px' }}
                                      />
                                    ))}
                                  </Stack>
                                  {intervention?.therapy?.attachPrescriptionForTherapy && (
                                    <Chip
                                      label="Prescription Attached"
                                      size="small"
                                      color="success"
                                      sx={{ mt: 1, fontSize: '0.7rem', height: '20px' }}
                                    />
                                  )}
                                  {intervention?.therapy?.notesToTherapist && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        Notes to Therapist:
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {intervention.therapy.notesToTherapist}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* Assessment Section */}
                              {hasAssessmentIntervention(intervention) && (
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="caption"
                                    color="secondary"
                                    sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                                  >
                                    <AssignmentIcon sx={{ fontSize: 16 }} />
                                    Assessment
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {intervention?.assessment?.prescribedAssessments?.map((assessment, idx) => (
                                      <Chip
                                        key={`prescribed-assessment-${idx}`}
                                        label={assessment}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: '22px' }}
                                      />
                                    ))}
                                    {intervention?.assessment?.psychAssessments?.map((psych, idx) => (
                                      <Chip
                                        key={`psych-assessment-${idx}`}
                                        label={psych?.title || psych?.id}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: '22px' }}
                                      />
                                    ))}
                                  </Stack>
                                  {intervention?.assessment?.attachPrescriptionForAssessment && (
                                    <Chip
                                      label="Prescription Attached"
                                      size="small"
                                      color="success"
                                      sx={{ mt: 1, fontSize: '0.7rem', height: '20px' }}
                                    />
                                  )}
                                  {intervention?.assessment?.notesForAssessment && (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        Notes for Assessment:
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {intervention.assessment.notesForAssessment}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                          </Box>
                        </Box>
                    </CardContent>
                  </InterventionCard>
                  )
                })}
              </Stack>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page + 1}
                    onChange={(event, value) => changePage(value - 1)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {/* Results count */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {interventions.length} of {pagination.totalElements} intervention
                  {pagination.totalElements !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Action Drawer */}
      <InterventionActionDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        intervention={selectedIntervention}
      />

      {/* Followups Drawer */}
      {selectedInterventionForFollowups && (
        <FollowupsDrawer
          open={followupsDrawerOpen}
          onClose={handleCloseFollowupsDrawer}
          intervention={selectedInterventionForFollowups}
        />
      )}
    </>
  )
}

